// Shared helpers + small components used across the mobile app view tabs.
// Extracted from the original monolithic MobileApp.jsx during the P1
// refactor. Nothing here holds view-level state — these are pure UI atoms
// (StatCard, Slider, SubHeader, …) and small numeric utilities.

import React, { useMemo, useState } from "react";
import {
  BatteryCharging,
  Bell,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  Sun,
  Thermometer,
  TrendingDown,
  Zap,
  X,
  Check,
  Lock,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  CartesianGrid,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from "recharts";
import { buildDegradationSeries } from "../../../lib/vehicles";
import { useSubscription, openSubscribeGate } from "../../../lib/subscription";
import { BatteryLifePredictor } from "../BatteryLifePredictor";

// -------- Constants --------
export const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export const VEHICLE_USAGE_HINTS = {
  "model-3-lr":         { supercharger_pct: 28, parked_at_100pct_days_per_month: 3, preconditions_in_winter: true },
  "model-s-lr":         { supercharger_pct: 22, parked_at_100pct_days_per_month: 1, preconditions_in_winter: true },
  "model-s-plaid-2021": { supercharger_pct: 42, parked_at_100pct_days_per_month: 5, preconditions_in_winter: true },
  "model-y-lr":         { supercharger_pct: 30, parked_at_100pct_days_per_month: 2, preconditions_in_winter: true },
  "model-x-plaid":      { supercharger_pct: 38, parked_at_100pct_days_per_month: 4, preconditions_in_winter: true },
  "cybertruck":         { supercharger_pct: 48, parked_at_100pct_days_per_month: 6, preconditions_in_winter: false },
  "roadster-2026":      { supercharger_pct: 55, parked_at_100pct_days_per_month: 4, preconditions_in_winter: true },
};

export const recordWarrantyClaim = (soh) => {
  try {
    fetch(`${API_BASE}/api/stats/warranty-claim`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ soh_pct: soh }),
    });
  } catch (_e) {
    // best-effort telemetry
  }
};

export const FACTOR_COLORS = {
  soh: "#39FF14",
  slope: "#00E5FF",
  climate: "#39FF14",
  charging: "#00E5FF",
  thermal: "#FFB020",
  anomalies: "#FF6B57",
};

export const FACTOR_META = {
  soh: {
    icon: BatteryCharging,
    what: "EVIQ Estimated SoH — your pack's current usable capacity versus a brand-new one, estimated from manufacturer telemetry.",
    drivers: [
      "Calendar age (all packs lose ~1–2%/year from just sitting)",
      "Total lifetime cycles (miles driven vs pack size)",
      "Time spent at 100% or 0% state-of-charge",
    ],
    improve: [
      "Cap daily charging at 80–90% unless you need range that day",
      "Avoid multi-day storage at high SoC (park at 50–60%)",
      "Precondition before Supercharging",
    ],
  },
  slope: {
    icon: TrendingDown,
    what: "How fast you're losing capacity right now — the 30-day trend, projected forward.",
    drivers: [
      "Frequency of DC-fast-charging sessions",
      "Ambient temperature over the last month",
      "Driving style (high-speed cruise vs city)",
    ],
    improve: [
      "Shift ≥60% of charging to home L2",
      "Reduce hard-accel + sustained 80mph+ cruising",
      "Precondition the pack before every DC charge",
    ],
  },
  climate: {
    icon: Thermometer,
    what: "How your local climate affects long-term degradation — hot/arid regions age batteries faster than mild coastal.",
    drivers: [
      "Summer high temperatures where you park",
      "Winter low temperatures & cold-soak duration",
      "Shade / covered garage availability",
    ],
    improve: [
      "Park in shade or a garage whenever possible",
      "Enable Cabin Overheat Protection in summer",
      "Precondition before short winter trips",
    ],
  },
  charging: {
    icon: Zap,
    what: "Your charging habits — mix of L2 (slow, gentle) vs Supercharger (fast, hot on the pack).",
    drivers: [
      "Percentage of miles from Superchargers",
      "Typical daily charge ceiling (80% vs 100%)",
      "Days per month parked at 100%",
    ],
    improve: [
      "Aim for Supercharger usage under 20% of total kWh",
      "Set the daily charge limit to 80%",
      "Charge to 100% only right before a road trip",
    ],
  },
  thermal: {
    icon: Sun,
    what: "Thermal stress — heat is the #1 accelerant of battery aging.",
    drivers: [
      "Ambient temp during DC charging",
      "Sustained highway climbs (heat + cycle stress)",
      "Cabin AC load during summer",
    ],
    improve: [
      "Avoid DC-fast-charging in direct summer sun",
      "Precondition before towing or grade climbs",
      "Turn on Sentry Mode less in hot parking lots",
    ],
  },
  anomalies: {
    icon: Bell,
    what: "Anything unusual EVIQ has flagged in the last 30 days — sudden slope changes, phantom drain, cell imbalance.",
    drivers: [
      "Cell voltage delta vs pack median",
      "Phantom drain vs sentry / preconditioning schedule",
      "Sudden capacity drops without matching usage",
    ],
    improve: [
      "Run a full charge-to-discharge cycle every 3 months",
      "Report to EVIQ if a drop persists >2 weeks",
      "Check Sentry / cabin overheat schedules",
    ],
  },
};

// -------- Small UI atoms --------

export const StatCard = ({ label, value, trend, color, live }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 relative">
    {live && (
      <span
        data-testid="stat-live-badge"
        className="absolute top-2 right-2 inline-flex items-center gap-0.5 text-[7px] font-mono uppercase tracking-[0.14em] text-[#39FF14]"
        title="Live from Tesla"
      >
        <span className="w-1 h-1 rounded-full bg-[#39FF14] animate-pulse" aria-hidden="true" />
        Live
      </span>
    )}
    {/* pr-9 leaves clear space so the trend text can't tuck under the
        absolutely-positioned "Live" badge on narrow (grid-cols-2) tiles. */}
    <div className={`font-mono text-[9px] uppercase tracking-wider text-zinc-500 ${live ? "pr-9" : ""}`}>
      {label}
    </div>
    <div className="font-display text-xl text-white font-semibold mt-1" style={{ color }}>
      {value}
    </div>
    <div className={`font-mono text-[10px] text-zinc-500 mt-0.5 ${live ? "pr-9" : ""}`}>{trend}</div>
  </div>
);

export const FactorRow = ({ label, pct, note, color }) => (
  <div>
    <div className="flex items-baseline justify-between mb-1">
      <span className="text-xs text-zinc-300">{label}</span>
      <span className="font-mono text-[10px] text-zinc-500">{note}</span>
    </div>
    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
    </div>
  </div>
);

export const Slider = ({ label, value, min, max, step, onChange, suffix, testid }) => (
  <div>
    <div className="flex items-baseline justify-between mb-1">
      <span className="font-mono text-[9px] uppercase text-zinc-500">{label}</span>
      <span className="font-mono text-[11px] text-white">
        {value}
        {suffix}
      </span>
    </div>
    <input
      type="range"
      value={value}
      min={min}
      max={max}
      step={step}
      onChange={(e) => onChange(Number(e.target.value))}
      data-testid={testid}
      className="w-full accent-[#39FF14]"
    />
  </div>
);

export const LabeledInput = ({ label, value, onChange, placeholder, testid }) => (
  <div>
    <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 mb-1">
      {label}
    </div>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full bg-black border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#39FF14]/40"
    />
  </div>
);

export const TripStat = ({ label, val, color = "#fff" }) => (
  <div className="text-center">
    <div className="font-mono text-[9px] uppercase text-zinc-500">{label}</div>
    <div className="text-sm font-mono mt-0.5" style={{ color }}>
      {val}
    </div>
  </div>
);

// Small green pulse chip used across the mobile app to mark any card
// whose numbers came from the Tesla OAuth Fleet API in this session.
// Placed in the top-right corner of any container that renders live
// values so users can tell at a glance what's real vs demo.
export const LiveChip = ({ testid = "live-chip", label = "Live", title = "Live from Tesla" }) => (
  <span
    data-testid={testid}
    className="inline-flex items-center gap-1 text-[8px] font-mono uppercase tracking-[0.14em] rounded-full px-1.5 py-0.5 border border-[#39FF14]/50 text-[#39FF14] bg-[#39FF14]/[0.10]"
    title={title}
  >
    <span
      className="w-1 h-1 rounded-full bg-[#39FF14] animate-pulse"
      aria-hidden="true"
    />
    {label}
  </span>
);

export const MoreTile = ({ icon: Icon, color, title, desc, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="w-full text-left rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-colors px-3 py-3 flex items-center gap-3"
  >
    <div
      className="w-9 h-9 rounded-lg flex items-center justify-center border border-white/10 shrink-0"
      style={{ background: `${color}15`, borderColor: `${color}30` }}
    >
      <Icon className="w-4 h-4" style={{ color }} strokeWidth={2.5} />
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-sm text-white font-semibold">{title}</div>
      <div className="text-[11px] text-zinc-500 truncate">{desc}</div>
    </div>
    <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0" strokeWidth={2} />
  </button>
);

export const PremiumTile = ({ icon: Icon, color, title, desc, locked, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`w-full text-left rounded-xl border transition-colors px-3 py-3 flex items-center gap-3 ${
      locked
        ? "border-white/[0.07] bg-white/[0.015] hover:bg-white/[0.03]"
        : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"
    }`}
  >
    <div
      className="w-9 h-9 rounded-lg flex items-center justify-center border shrink-0"
      style={{
        background: locked ? "rgba(255,255,255,0.02)" : `${color}15`,
        borderColor: locked ? "rgba(255,255,255,0.08)" : `${color}30`,
      }}
    >
      <Icon
        className="w-4 h-4"
        style={{ color: locked ? "#52525b" : color }}
        strokeWidth={2.5}
      />
    </div>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-1.5">
        <span
          className={`text-sm font-semibold truncate ${
            locked ? "text-zinc-400" : "text-white"
          }`}
        >
          {title}
        </span>
        {locked && (
          <Lock className="w-3 h-3 text-[#FFB020] shrink-0" strokeWidth={2.5} />
        )}
      </div>
      <div className="text-[11px] text-zinc-500 truncate">{desc}</div>
    </div>
    <ChevronRight
      className={`w-4 h-4 shrink-0 ${locked ? "text-zinc-600" : "text-zinc-500"}`}
      strokeWidth={2}
    />
  </button>
);

export const SubHeader = ({ title, back }) => (
  <div className="flex items-center gap-2 pt-4 pb-2">
    <button
      onClick={back}
      className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/5"
      aria-label="Back"
      data-testid="mobile-app-subview-back"
    >
      <ChevronLeft className="w-4 h-4 text-zinc-400" strokeWidth={2.5} />
    </button>
    <span className="text-sm text-white font-semibold">{title}</span>
  </div>
);

export const AlertCard = ({ icon: Icon, color, title, body, time, unread = false }) => (
  <div
    className={`rounded-xl p-3 border transition-colors ${
      unread ? "bg-white/[0.04] border-white/10" : "bg-white/[0.02] border-white/5"
    }`}
  >
    <div className="flex items-start gap-3">
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: `${color}15`, border: `1px solid ${color}40` }}
      >
        <Icon className="w-4 h-4" style={{ color }} strokeWidth={2.5} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm text-white font-semibold truncate flex items-center gap-1.5">
            {title}
            {unread && (
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
            )}
          </span>
          <span className="font-mono text-[10px] text-zinc-500 shrink-0">{time}</span>
        </div>
        <div className="text-[11px] text-zinc-400 leading-relaxed mt-1">{body}</div>
      </div>
    </div>
  </div>
);

export const Recommendation = ({ priority, title, why, impact }) => {
  const color =
    priority === "high" ? "#39FF14" : priority === "medium" ? "#FFB020" : "#71717A";
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
      <div className="flex items-center gap-2 mb-1.5">
        <span
          className="font-mono text-[9px] uppercase px-1.5 py-0.5 rounded border"
          style={{ color, background: `${color}15`, borderColor: `${color}40` }}
        >
          {priority}
        </span>
        <span className="text-sm text-white font-medium">{title}</span>
      </div>
      <p className="text-[11px] text-zinc-400 leading-relaxed mb-1.5">{why}</p>
      <div className="font-mono text-[10px]" style={{ color }}>
        {impact}
      </div>
    </div>
  );
};

// -------- Health chart (18-month SoH curve) --------

export const HealthChart = ({ vehicle, height = 120, testid }) => {
  const data = useMemo(() => buildDegradationSeries(vehicle, 18), [vehicle]);
  const floor = vehicle.warranty?.threshold || 70;
  const minSoh = Math.min(...data.map((d) => d.soh)) - 1.5;
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 relative" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-2 px-1 gap-2">
        <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 inline-flex items-center gap-1.5 min-w-0">
          Battery health · 18 mo
          {vehicle.live && (
            <span
              data-testid="health-chart-live"
              className="inline-flex items-center gap-1 text-[8px] font-mono uppercase tracking-[0.14em] rounded-full px-1.5 py-0.5 border border-[#39FF14]/50 text-[#39FF14] bg-[#39FF14]/[0.10] shrink-0"
              title="Live from Tesla"
            >
              <span className="w-1 h-1 rounded-full bg-[#39FF14] animate-pulse" aria-hidden="true" />
              Live
            </span>
          )}
        </span>
        <span className="font-mono text-[10px] text-zinc-400 shrink-0">
          {vehicle.sohCurrent}%{" "}
          <span className="text-zinc-600">· fleet {vehicle.fleetAvg}%</span>
        </span>
      </div>
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id={`sohGrad-${vehicle.id}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#39FF14" stopOpacity={0.5} />
              <stop offset="100%" stopColor="#39FF14" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#ffffff08" vertical={false} />
          <XAxis
            dataKey="month"
            tick={{ fill: "#71717a", fontSize: 8, fontFamily: "monospace" }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
          />
          <YAxis
            domain={[Math.max(60, minSoh), 100]}
            tick={{ fill: "#71717a", fontSize: 8, fontFamily: "monospace" }}
            axisLine={false}
            tickLine={false}
            width={22}
          />
          <Tooltip
            contentStyle={{
              background: "#0a0a0a",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 8,
              fontSize: 11,
            }}
            labelStyle={{ color: "#a1a1aa", fontFamily: "monospace" }}
          />
          <ReferenceLine
            y={floor}
            stroke="#FFB020"
            strokeDasharray="3 3"
            label={{ value: `${floor}% floor`, fill: "#FFB020", fontSize: 8, position: "insideTopRight" }}
          />
          <Area
            type="monotone"
            dataKey="fleet"
            stroke="#00E5FF"
            strokeWidth={1.2}
            strokeDasharray="4 3"
            fill="transparent"
            isAnimationActive={false}
          />
          <Area
            type="monotone"
            dataKey="soh"
            stroke="#39FF14"
            strokeWidth={2}
            fill={`url(#sohGrad-${vehicle.id})`}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// -------- Predictor slot (gated by subscription tier) --------

export const PredictorSlot = (props) => {
  const { hasPremium } = useSubscription();
  if (hasPremium) return <BatteryLifePredictor {...props} />;
  return (
    <div
      data-testid="battery-life-predictor-locked"
      className="rounded-2xl border p-4 sm:p-5 bg-gradient-to-br from-[#00E5FF]/[0.03] to-transparent"
      style={{ borderColor: "rgba(0,229,255,0.25)" }}
    >
      <div className="flex items-start justify-between mb-3 gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <div
              className="w-6 h-6 rounded-md flex items-center justify-center shrink-0 border"
              style={{ background: "rgba(0,229,255,0.1)", borderColor: "rgba(0,229,255,0.35)" }}
            >
              <Lock className="w-3 h-3" style={{ color: "#00E5FF" }} strokeWidth={2.2} />
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              Battery Life Predictor
            </div>
          </div>
          <div className="text-white text-base font-semibold leading-tight">
            Unlock years-of-life-remaining forecasts.
          </div>
          <div className="text-[12px] text-zinc-400 mt-1 leading-relaxed">
            Yearly and Lifetime plans include the Battery Life Predictor,
            AI narrative, and service-ready warranty PDF — the analytics
            other EV apps don&rsquo;t ship.
          </div>
        </div>
      </div>
      <button
        onClick={() => openSubscribeGate("predictor-locked")}
        data-testid="predictor-upgrade-cta"
        className="w-full brand-gradient text-black font-semibold rounded-xl py-2.5 text-sm inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
      >
        Upgrade to Yearly · $99.99/yr
      </button>
    </div>
  );
};

// -------- Radar / factor detail --------

const RadarAxisTick = ({ payload, x, y, textAnchor, factorData }) => {
  const row = factorData.find((d) => d.label === payload.value);
  const color = row?.color || "#71717a";
  return (
    <text
      x={x}
      y={y}
      textAnchor={textAnchor}
      fill={color}
      fontSize={9}
      fontFamily="monospace"
      style={{ letterSpacing: "0.02em" }}
    >
      {payload.value}
    </text>
  );
};

export const SubScoreRadarChart = ({ factors, testid, height = 240, isLive = false }) => {
  const [detail, setDetail] = useState(null);

  const data = useMemo(
    () => factors.map((f) => ({
      key: f.key,
      label: f.label,
      score: Math.round(f.raw),
      color: FACTOR_COLORS[f.key] || "#39FF14",
      full: 100,
    })),
    [factors]
  );

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 relative" data-testid={testid}>
      {isLive && (
        <span
          data-testid="subscore-live"
          className="absolute top-2 right-2 inline-flex items-center gap-1 text-[8px] font-mono uppercase tracking-[0.14em] rounded-full px-1.5 py-0.5 border border-[#39FF14]/50 text-[#39FF14] bg-[#39FF14]/[0.10]"
          title="Live from Tesla"
        >
          <span className="w-1 h-1 rounded-full bg-[#39FF14] animate-pulse" aria-hidden="true" />
          Live
        </span>
      )}
      <div className="flex items-baseline justify-between mb-2 px-1">
        <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
          Sub-scores · balance view
        </span>
        <span className="font-mono text-[10px] text-zinc-500">tap a factor →</span>
      </div>

      <ResponsiveContainer width="100%" height={height}>
        <RadarChart data={data} outerRadius="72%">
          <defs>
            <radialGradient id="radarFill" cx="50%" cy="50%" r="70%">
              <stop offset="0%" stopColor="#39FF14" stopOpacity={0.35} />
              <stop offset="100%" stopColor="#00E5FF" stopOpacity={0.18} />
            </radialGradient>
          </defs>
          <PolarGrid stroke="#ffffff14" />
          <PolarAngleAxis
            dataKey="label"
            /* eslint-disable-next-line react/no-unstable-nested-components */
            tick={(props) => <RadarAxisTick {...props} factorData={data} />}
          />
          <PolarRadiusAxis
            angle={90}
            domain={[0, 100]}
            tick={{ fill: "#52525b", fontSize: 8, fontFamily: "monospace" }}
            axisLine={false}
            tickCount={5}
          />
          <Radar
            name="score"
            dataKey="score"
            stroke="#39FF14"
            strokeWidth={2}
            fill="url(#radarFill)"
            fillOpacity={0.9}
            isAnimationActive={true}
            animationDuration={600}
          />
          <Tooltip
            contentStyle={{
              background: "#0a0a0a",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 8,
              fontSize: 10,
            }}
            labelStyle={{ color: "#a1a1aa", fontFamily: "monospace" }}
            itemStyle={{ fontFamily: "monospace", fontSize: 10, color: "#39FF14" }}
            formatter={(value) => [`${value}/100`, "Score"]}
          />
        </RadarChart>
      </ResponsiveContainer>

      <div
        className="grid grid-cols-2 gap-1.5 mt-2"
        data-testid="mobile-app-subscore-legend"
      >
        {data.map((row) => (
          <button
            key={row.key}
            onClick={() => setDetail(factors.find((f) => f.key === row.key))}
            data-testid={`subscore-factor-${row.key}`}
            className="flex items-center gap-2 rounded-lg border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.06] transition-colors px-2 py-1.5 text-left"
          >
            <span
              className="w-2 h-2 rounded-sm shrink-0"
              style={{ background: row.color }}
            />
            <span className="text-[10px] text-zinc-300 truncate flex-1">
              {row.label}
            </span>
            <span className="font-mono text-[10px] text-white">{row.score}</span>
            <ChevronRight className="w-3 h-3 text-zinc-500 shrink-0" strokeWidth={2} />
          </button>
        ))}
      </div>

      {detail && (
        <FactorDetailSheet
          factor={detail}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
};

const FactorDetailSheet = ({ factor, onClose }) => {
  const color = FACTOR_COLORS[factor.key] || "#39FF14";
  const meta = FACTOR_META[factor.key] || {
    icon: Sparkles,
    what: factor.note || factor.label,
    drivers: [],
    improve: [],
  };
  const Icon = meta.icon;
  const score = Math.round(factor.raw);
  const rating =
    score >= 88 ? "Excellent" : score >= 74 ? "Good" : score >= 60 ? "Watch" : "Needs attention";

  return (
    <div
      className="absolute inset-0 z-30 flex items-end bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="subscore-detail-sheet"
    >
      <div
        className="w-full bg-[#0a0a0a] border-t border-white/10 rounded-t-2xl max-h-[85%] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-white/5">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center border shrink-0"
            style={{ background: `${color}12`, borderColor: `${color}44` }}
          >
            <Icon className="w-5 h-5" style={{ color }} strokeWidth={2} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm text-white font-semibold truncate">
              {factor.label}
            </div>
            <div className="font-mono text-[10px] text-zinc-500">
              {factor.note}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-white/5 shrink-0"
            aria-label="Close"
            data-testid="subscore-detail-close"
          >
            <X className="w-4 h-4 text-zinc-400" strokeWidth={2} />
          </button>
        </div>

        <div className="overflow-y-auto px-4 py-3 space-y-4">
          <div>
            <div className="flex items-baseline gap-3">
              <span className="font-display text-4xl text-white font-semibold" data-testid="subscore-detail-value">
                {score}
              </span>
              <span className="font-mono text-xs text-zinc-500">/ 100</span>
              <span
                className="ml-auto font-mono text-[10px] uppercase px-2 py-0.5 rounded-full border"
                style={{ color, borderColor: `${color}55`, background: `${color}10` }}
              >
                {rating}
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-white/5 overflow-hidden mt-3">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${score}%`, background: color }}
              />
            </div>
          </div>

          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">
              What this measures
            </div>
            <p className="text-[12px] text-zinc-300 leading-relaxed">{meta.what}</p>
          </div>

          {meta.drivers.length > 0 && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">
                What&apos;s driving it
              </div>
              <ul className="space-y-1.5">
                {meta.drivers.map((d, i) => (
                  <li key={i} className="flex items-start gap-2 text-[11px] text-zinc-300 leading-snug">
                    <span
                      className="w-1 h-1 rounded-full mt-1.5 shrink-0"
                      style={{ background: color }}
                    />
                    {d}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {meta.improve.length > 0 && (
            <div className="rounded-xl border p-3" style={{ borderColor: `${color}33`, background: `${color}08` }}>
              <div className="font-mono text-[10px] uppercase tracking-wider mb-1.5" style={{ color }}>
                How to improve
              </div>
              <ul className="space-y-1.5">
                {meta.improve.map((d, i) => (
                  <li key={i} className="flex items-start gap-2 text-[11px] text-white leading-snug">
                    <Check className="w-3 h-3 mt-0.5 shrink-0" style={{ color }} strokeWidth={3} />
                    {d}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// -------- Trip helpers --------

export const TRIP_PRESETS = [
  { key: "sf-tahoe", origin: "San Francisco", dest: "South Lake Tahoe", miles: 196 },
  { key: "la-vegas", origin: "Los Angeles", dest: "Las Vegas", miles: 270 },
  { key: "nyc-boston", origin: "New York", dest: "Boston", miles: 215 },
  { key: "sea-portland", origin: "Seattle", dest: "Portland", miles: 174 },
  { key: "chi-detroit", origin: "Chicago", dest: "Detroit", miles: 283 },
  { key: "sf-la", origin: "San Francisco", dest: "Los Angeles", miles: 383 },
  { key: "la-sd", origin: "Los Angeles", dest: "San Diego", miles: 121 },
  { key: "sf-reno", origin: "San Francisco", dest: "Reno", miles: 218 },
  { key: "dc-nyc", origin: "Washington DC", dest: "New York", miles: 227 },
  { key: "atl-nash", origin: "Atlanta", dest: "Nashville", miles: 250 },
  { key: "mia-orl", origin: "Miami", dest: "Orlando", miles: 235 },
  { key: "aus-hou", origin: "Austin", dest: "Houston", miles: 165 },
  { key: "aus-dal", origin: "Austin", dest: "Dallas", miles: 195 },
  { key: "phx-tucson", origin: "Phoenix", dest: "Tucson", miles: 116 },
  { key: "custom", origin: "", dest: "", miles: 150 },
];

const CITY_COORDS = {
  "san francisco": [37.7749, -122.4194],
  "sf": [37.7749, -122.4194],
  "los angeles": [34.0522, -118.2437],
  "la": [34.0522, -118.2437],
  "san diego": [32.7157, -117.1611],
  "las vegas": [36.1699, -115.1398],
  "reno": [39.5296, -119.8138],
  "south lake tahoe": [38.9399, -119.9772],
  "sacramento": [38.5816, -121.4944],
  "portland": [45.5152, -122.6784],
  "seattle": [47.6062, -122.3321],
  "phoenix": [33.4484, -112.0740],
  "tucson": [32.2226, -110.9747],
  "denver": [39.7392, -104.9903],
  "austin": [30.2672, -97.7431],
  "dallas": [32.7767, -96.7970],
  "houston": [29.7604, -95.3698],
  "chicago": [41.8781, -87.6298],
  "detroit": [42.3314, -83.0458],
  "new york": [40.7128, -74.0060],
  "nyc": [40.7128, -74.0060],
  "boston": [42.3601, -71.0589],
  "washington dc": [38.9072, -77.0369],
  "atlanta": [33.7490, -84.3880],
  "nashville": [36.1627, -86.7816],
  "miami": [25.7617, -80.1918],
  "orlando": [28.5383, -81.3792],
};

function haversineMiles(a, b) {
  const toRad = (d) => (d * Math.PI) / 180;
  const [lat1, lon1] = a;
  const [lat2, lon2] = b;
  const R = 3958.8;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(s)) * 1.15);
}

export function estimateMiles(origin, dest) {
  const o = CITY_COORDS[(origin || "").trim().toLowerCase()];
  const d = CITY_COORDS[(dest || "").trim().toLowerCase()];
  if (!o || !d) return null;
  return haversineMiles(o, d);
}

// -------- Cell health helpers --------

export function seededRandom(seed) {
  let s = 0;
  for (let i = 0; i < seed.length; i++) s = (s * 31 + seed.charCodeAt(i)) | 0;
  return () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
}

export function buildCells(vehicle, count = 120) {
  // In a real Tesla pack, the pack-level SoH is limited by the WEAKEST
  // modules — not the average. So we model cells with a small skew above
  // the pack mean (most cells still healthy, a minority pulling the
  // pack SoH down). Without this, a 74% mean pack would paint every
  // single cell red, which is dramatically misleading.
  const mean = vehicle.sohCurrent;
  const skew = 5; // most cells are ~5% above the pack-limiting minimum
  const cellMean = Math.min(100, mean + skew);
  const spread = Math.min(6, 2.5 + Math.abs(vehicle.delta30d || 0) * 4);
  const rand = seededRandom(`${vehicle.id}-${vehicle.year}`);
  const cells = [];
  for (let i = 0; i < count; i++) {
    const u = rand() || 0.001;
    const v = rand() || 0.001;
    const z = Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
    // Clamp lower bound to the pack mean so we don't fabricate cells
    // healthier than what the pack telemetry actually reports as its
    // limiting capacity.
    const health = Math.max(mean - 4, Math.min(100, cellMean + z * spread));
    let bucket;
    if (health >= 88) bucket = "healthy";
    else if (health >= 78) bucket = "watch";
    else bucket = "critical";
    const voltage = 3.6 + (health / 100) * 0.6;
    cells.push({ i, health: +health.toFixed(1), voltage: +voltage.toFixed(2), bucket });
  }
  return cells;
}

export const BUCKETS = {
  healthy: { color: "#39FF14", label: "Healthy", desc: "≥ 88% capacity" },
  watch: { color: "#FFB020", label: "Watch", desc: "78 – 88%" },
  critical: { color: "#FF6B57", label: "Critical", desc: "< 78%" },
};
